import { useState, useRef, useCallback } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/firebaseConfig';

/* Ported verbatim from OLD Support/useImageUpload.js.

   Validates images (type, size), tracks per-image upload progress, and
   uploads to Firebase Storage at
     supportMessages/{accountUUID}/{messageId}/{imageId}_{filename}
   Returns per-image metadata { url, name, size, storagePath, width,
   height } the caller then writes onto the message document.
*/

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function generateId() {
  return Date.now() + '-' + Math.random().toString(36).slice(2);
}

function getImageDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

export function useImageUpload() {
  const [pendingImages, setPendingImages] = useState([]);
  const uploadTasksRef = useRef([]);

  const addImages = useCallback(async (files) => {
    const fileArray = Array.from(files);
    const errors = [];

    const validFiles = [];
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        errors.push(`"${file.name}" is not an image`);
        continue;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" \u2014 unsupported image type`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds 5MB limit`);
        continue;
      }
      validFiles.push(file);
    }

    setPendingImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      if (remaining <= 0) {
        errors.push(`Maximum ${MAX_IMAGES} images per message`);
        return prev;
      }
      const toAdd = validFiles.slice(0, remaining);
      if (toAdd.length < validFiles.length) {
        errors.push(`Only ${remaining} more image${remaining > 1 ? 's' : ''} can be added`);
      }

      const newImages = toAdd.map((file) => ({
        id: generateId(),
        file,
        previewUrl: URL.createObjectURL(file),
        width: 0,
        height: 0,
        status: 'pending',
        progress: 0,
        error: null,
      }));

      newImages.forEach(async (img) => {
        const dims = await getImageDimensions(img.file);
        setPendingImages((current) =>
          current.map((p) => (p.id === img.id ? { ...p, width: dims.width, height: dims.height } : p)),
        );
      });

      return [...prev, ...newImages];
    });

    return errors;
  }, []);

  const removeImage = useCallback((id) => {
    setPendingImages((prev) => {
      const img = prev.find((p) => p.id === id);
      if (img) URL.revokeObjectURL(img.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const clearImages = useCallback(() => {
    setPendingImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    uploadTasksRef.current = [];
  }, []);

  const uploadImages = useCallback(
    async (accountUUID, messageId) => {
      const images = [...pendingImages];
      if (images.length === 0) return [];

      uploadTasksRef.current = [];

      const uploadPromises = images.map((img) =>
        new Promise((resolve, reject) => {
          const storagePath = `supportMessages/${accountUUID}/${messageId}/${img.id}_${img.file.name}`;
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, img.file, {
            contentType: img.file.type,
          });

          uploadTasksRef.current.push(uploadTask);

          setPendingImages((prev) =>
            prev.map((p) => (p.id === img.id ? { ...p, status: 'uploading' } : p)),
          );

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setPendingImages((prev) =>
                prev.map((p) => (p.id === img.id ? { ...p, progress } : p)),
              );
            },
            (err) => {
              setPendingImages((prev) =>
                prev.map((p) => (p.id === img.id ? { ...p, status: 'error', error: err.message } : p)),
              );
              reject(err);
            },
            async () => {
              try {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                setPendingImages((prev) =>
                  prev.map((p) => (p.id === img.id ? { ...p, status: 'done', progress: 100 } : p)),
                );
                resolve({
                  url,
                  name: img.file.name,
                  size: img.file.size,
                  storagePath,
                  width: img.width,
                  height: img.height,
                });
              } catch (err) {
                setPendingImages((prev) =>
                  prev.map((p) => (p.id === img.id ? { ...p, status: 'error', error: err.message } : p)),
                );
                reject(err);
              }
            },
          );
        }),
      );

      return Promise.all(uploadPromises);
    },
    [pendingImages],
  );

  const cancelUploads = useCallback(() => {
    uploadTasksRef.current.forEach((task) => {
      try { task.cancel(); } catch (_) { /* ignore */ }
    });
    uploadTasksRef.current = [];
  }, []);

  return {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    uploadImages,
    cancelUploads,
    hasImages: pendingImages.length > 0,
  };
}
